import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Search, ChevronDown, ChevronUp, Receipt } from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const inputCls = "bg-zinc-950 border-zinc-800 font-mono";

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-base font-bold font-mono">{value ?? "—"}</div>
    </div>
  );
}

function ReceiptRow({ r }) {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState(null);
  const toggle = async () => {
    if (!open && lines === null) {
      try { const { data } = await api.get(`/elastic/receipts/${r.id}/details`); setLines(data); }
      catch (e) { toast.error(formatApiError(e)); setLines([]); }
    }
    setOpen(!open);
  };
  return (
    <>
      <tr className="border-t border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer" onClick={toggle} data-testid={`elastic-receipt-${r.id}`}>
        <td className="p-2 font-mono">{r.code}</td>
        <td className="p-2 whitespace-nowrap">{r.datetime}</td>
        <td className="p-2">{r.type}{r.causal ? ` · ${r.causal}` : ""}</td>
        <td className="p-2 text-right font-mono">{r.items}</td>
        <td className="p-2 text-right font-mono">{eur(r.total)}</td>
        <td className="p-2 text-right font-mono text-emerald-400">{r.points ? `+${r.points}` : "—"}</td>
        <td className="p-2 text-right font-mono text-red-400">{r.points_used ? `-${r.points_used}` : "—"}</td>
        <td className="p-2 text-zinc-500">{r.operator}</td>
        <td className="p-2 text-zinc-500">{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</td>
      </tr>
      {open && (
        <tr className="bg-zinc-950/60" data-testid={`elastic-receipt-details-${r.id}`}>
          <td colSpan={9} className="p-3">
            {lines === null ? <span className="text-xs text-zinc-500">Carico righe…</span>
              : lines.length === 0 ? <span className="text-xs text-zinc-500">Nessuna riga prodotto per questo scontrino.</span>
              : (
                <table className="w-full text-[11px]">
                  <thead className="text-zinc-500 uppercase tracking-wider text-[9px]"><tr><th className="text-left p-1">#</th><th className="text-left p-1">Prodotto</th><th className="text-left p-1">EAN</th><th className="text-right p-1">Q.tà</th><th className="text-right p-1">Prezzo</th><th className="text-right p-1">Sconto</th><th className="text-right p-1">Totale</th><th className="text-right p-1">Punti</th></tr></thead>
                  <tbody>{lines.map((l, i) => (
                    <tr key={i} className="border-t border-zinc-800/40">
                      <td className="p-1 font-mono">{l.position}</td><td className="p-1">{l.name || "—"}{l.type && l.type !== "Normale" ? <span className="ml-1 text-zinc-500">({l.type})</span> : null}</td>
                      <td className="p-1 font-mono text-zinc-500">{l.ean || l.code || ""}</td><td className="p-1 text-right font-mono">{l.qty}{l.unit ? ` ${l.unit}` : ""}</td>
                      <td className="p-1 text-right font-mono">{eur(l.price)}</td><td className="p-1 text-right font-mono">{l.discount ? eur(l.discount) : "—"}</td>
                      <td className="p-1 text-right font-mono">{eur(l.total)}</td><td className="p-1 text-right font-mono">{l.points || "—"}</td>
                    </tr>))}</tbody>
                </table>
              )}
          </td>
        </tr>
      )}
    </>
  );
}

export const ElasticCardLookup = ({ initialCard = "", autoSearch = false, compact = false }) => {
  const [mode, setMode] = useState("card");
  const [card, setCard] = useState(initialCard);
  const [nameQ, setNameQ] = useState("");
  const [people, setPeople] = useState(null);
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const searchCard = async (c) => {
    const value = (c ?? card).trim();
    if (!value) return;
    setBusy(true); setData(null);
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const { data } = await api.get(`/elastic/card/${encodeURIComponent(value)}/receipts`, { params });
      setData(data);
      if (!data.found) toast.info("Nessuno scontrino trovato per questa carta nel CRM");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const searchPeople = async () => {
    if (nameQ.trim().length < 3) { toast.error("Inserisci almeno 3 caratteri"); return; }
    setBusy(true); setPeople(null); setData(null);
    try { const { data } = await api.get("/elastic/search", { params: { q: nameQ.trim() } }); setPeople(data.results); if (!data.results.length) toast.info("Nessun cliente CRM trovato"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const search = async (e) => { e?.preventDefault(); if (mode === "card") await searchCard(); else await searchPeople(); };
  const pickPerson = (p) => { setCard(p.card); setMode("card"); setPeople(null); searchCard(p.card); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoSearch && initialCard) searchCard(initialCard); }, [autoSearch, initialCard]);

  const cl = data?.client || {};
  const s = data?.summary || {};
  return (
    <section className={compact ? "space-y-4" : "card-elev p-5 space-y-4"} data-testid="elastic-card-lookup">
      {!compact && (
        <div>
          <h2 className="font-heading font-semibold flex items-center gap-2"><Receipt className="h-4 w-4 text-amber-500" /> Consulta movimenti CRM (sola lettura)</h2>
          <p className="text-xs text-zinc-500 mt-1">Interroga direttamente il CRM Elastic: nessun dato viene importato o modificato in Vivi.</p>
        </div>
      )}
      <div className="inline-flex rounded-full border border-zinc-800 p-0.5 text-xs" data-testid="elastic-lookup-mode">
        <button type="button" onClick={() => setMode("card")} data-testid="elastic-mode-card" className={`px-3 py-1 rounded-full ${mode === "card" ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400"}`}>Per carta</button>
        <button type="button" onClick={() => setMode("name")} data-testid="elastic-mode-name" className={`px-3 py-1 rounded-full ${mode === "name" ? "bg-[color:var(--paprika)] text-white" : "text-zinc-400"}`}>Per cognome / telefono</button>
      </div>
      <form onSubmit={search} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        {mode === "card" ? (
          <div className="sm:col-span-2"><Label className="text-zinc-400 text-xs">Codice carta</Label>
            <Input data-testid="elastic-lookup-card" value={card} onChange={(e) => setCard(e.target.value)} placeholder="es. 0400010116140" className={inputCls} /></div>
        ) : (
          <div className="sm:col-span-2"><Label className="text-zinc-400 text-xs">Cognome, nome o numero di telefono</Label>
            <Input data-testid="elastic-lookup-name" value={nameQ} onChange={(e) => setNameQ(e.target.value)} placeholder="es. Rossi Maria · 3331234567" className={inputCls} /></div>
        )}
        <div><Label className="text-zinc-400 text-xs">Dal</Label><Input type="date" data-testid="elastic-lookup-from" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className={inputCls} /></div>
        <div className="flex gap-2">
          <div className="flex-1"><Label className="text-zinc-400 text-xs">Al</Label><Input type="date" data-testid="elastic-lookup-to" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className={inputCls} /></div>
          <Button type="submit" disabled={busy || (mode === "card" ? !card.trim() : nameQ.trim().length < 3)} data-testid="elastic-lookup-submit" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] self-end"><Search className="h-4 w-4 mr-1" /> {busy ? "Cerco…" : "Cerca"}</Button>
        </div>
      </form>

      {people && people.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-zinc-800" data-testid="elastic-people-results">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px]"><tr><th className="text-left p-2">Cliente</th><th className="text-left p-2">Carta</th><th className="text-left p-2">Cell</th><th className="text-left p-2">Città</th><th className="text-right p-2">Scontrini</th><th className="text-right p-2">Speso</th><th className="text-left p-2">Ultima visita</th></tr></thead>
            <tbody>{people.map((p) => (
              <tr key={p.card} className="border-t border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer" onClick={() => pickPerson(p)} data-testid={`elastic-person-${p.card}`}>
                <td className="p-2 font-semibold">{p.name || "—"}{p.blocked ? <span className="ml-1 pill bg-red-500/15 text-red-400">bloccato</span> : null}</td>
                <td className="p-2 font-mono">{p.card}</td><td className="p-2 font-mono">{p.cell || "—"}</td><td className="p-2">{p.city || "—"}</td>
                <td className="p-2 text-right font-mono">{p.receipts}</td><td className="p-2 text-right font-mono">{eur(p.total_eur)}</td><td className="p-2">{p.last_visit || "—"}</td>
              </tr>))}</tbody>
          </table>
          <p className="p-2 text-[11px] text-zinc-500">Clicca un cliente per vedere i suoi scontrini.</p>
        </div>
      )}

      {data && data.found && (
        <div className="space-y-3" data-testid="elastic-lookup-result">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <div><span className="text-zinc-500">Cliente CRM:</span> <strong data-testid="elastic-lookup-client">{cl.fullName || "—"}</strong>{cl.blocked ? <span className="ml-2 pill bg-red-500/15 text-red-400">bloccato</span> : null}</div>
            {cl.cell && <div><span className="text-zinc-500">Cell:</span> <span className="font-mono">{cl.cell}</span></div>}
            {cl.email && <div><span className="text-zinc-500">Email:</span> {cl.email}</div>}
            <div><span className="text-zinc-500">Saldo punti CRM:</span> <strong className="font-mono">{cl.points ?? "—"}</strong></div>
            {cl.dateRegisterEU && <div><span className="text-zinc-500">Iscritto il:</span> {cl.dateRegisterEU}</div>}
            {data.store && <div><span className="text-zinc-500">Negozio:</span> {data.store}</div>}
            <div><span className="text-zinc-500">In Vivi:</span> {data.vivi_customer ? <span data-testid="elastic-lookup-vivi">{data.vivi_customer.name} · {data.vivi_customer.points} pt</span> : <span className="text-zinc-500">carta non presente</span>}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <Kpi label="Scontrini" value={data.count} testid="elastic-lookup-count" />
            <Kpi label="Speso totale" value={eur(s.total_eur)} testid="elastic-lookup-total" />
            <Kpi label="Punti guadagnati" value={s.points_assigned} testid="elastic-lookup-points" />
            <Kpi label="Punti usati" value={s.points_used} testid="elastic-lookup-used" />
            <Kpi label="Prima visita" value={s.first_visit} testid="elastic-lookup-first" />
            <Kpi label="Ultima visita" value={s.last_visit} testid="elastic-lookup-last" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-800">
            <table className="w-full text-xs" data-testid="elastic-lookup-table">
              <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px]">
                <tr><th className="text-left p-2">Scontrino</th><th className="text-left p-2">Data</th><th className="text-left p-2">Tipo</th><th className="text-right p-2">Art.</th><th className="text-right p-2">Totale</th><th className="text-right p-2">Punti</th><th className="text-right p-2">Usati</th><th className="text-left p-2">Operatore</th><th className="p-2"></th></tr>
              </thead>
              <tbody>{data.receipts.map((r) => <ReceiptRow key={r.id} r={r} />)}</tbody>
            </table>
          </div>
          {data.count > data.receipts.length && <p className="text-[11px] text-zinc-500">Mostrati gli ultimi {data.receipts.length} di {data.count} scontrini: restringi l'intervallo di date per vederli tutti.</p>}
          <p className="text-[11px] text-zinc-500">Clicca uno scontrino per vedere i prodotti acquistati.</p>
        </div>
      )}
    </section>
  );
};

export default ElasticCardLookup;
